import { AgGridAngular } from '@ag-grid-community/angular';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  AnalyticsService,
  HeaderRendererComponent,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import {
  ApiUrls,
  AppStateService,
  IUserProfile,
  WindowScrollService,
} from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TranslateService } from '@shared/translate/translate.service';
import {
  OrderHistDetail,
  OrderHistoryList,
  PaginationActionEnum,
} from '@types';
import { Logger } from '@utils/logger';
import { createDateComparator, stringCompare } from '@utils/sort-utils';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { mergeMap, shareReplay, takeUntil, retry } from 'rxjs/operators';
import { OrderNameCellRenderComponent } from './name-cell-render/order-name-cell-render.component';
import { NoRowOverlayRenderComponent } from './no-row-overlay-render/no-row-overlay-render.component';
import { OrderHistoryListService } from './services/order-history-list.service';
import { PersonalisationAPIService } from '../../../services/personalisation-api.service';

const logger = Logger.getLogger('OrderHistoryListComponent');

@Component({
  selector: 'ft-literature-order-history-list',
  templateUrl: './order-history-list.component.html',
  providers: [DatePipe],
})
export class OrderHistoryListComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  columnDefs = [];
  profile$: Observable<IUserProfile>;
  orderHistoryList$: Observable<OrderHistoryList>;
  orderHistoryList;
  isHandheld$: Observable<boolean>;
  sortByItems = [];
  rowNoItems = [];
  apiDateFormat = 'YYYY-MM-DD';
  paginationPageSize: number;
  pageSize: number;
  gridOptions = {
    domLayout: 'autoHeight',
    pagination: true,
    suppressRowHoverHighlight: true,
  };
  // number of total pages for pagination
  numberOfPages: number;
  currentPage: OrderHistDetail[];
  defaultColDef = {
    wrapText: true,
    headerComponentFramework: HeaderRendererComponent,
    autoHeight: true,
  };
  frameworkComponents: {
    customizedOrderNumber: typeof OrderNameCellRenderComponent;
    customNoRowsOverlay: typeof NoRowOverlayRenderComponent;
  };
  noRowsOverlayComponent = 'customNoRowsOverlay';
  @ViewChild('orderHistoryListGrid', { static: false })
  orderHistoryListGrid: AgGridAngular;
  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  private unsubscribe$: Subject<void> = new Subject<void>();
  pageTitle: string;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private dateFormatter: DatePipe,
    private orderHistoryListService: OrderHistoryListService,
    private responsiveService: ResponsiveService,
    protected translateService: TranslateService,
    private literatureConfig: LiteratureGlobalConfigService,
    private analytics: AnalyticsService,
    private titleService: Title,
    private scrollService: WindowScrollService,
    private personalisationService: PersonalisationAPIService
  ) {
    super();
    this.frameworkComponents = {
      customizedOrderNumber: OrderNameCellRenderComponent,
      customNoRowsOverlay: NoRowOverlayRenderComponent,
    };
  }

  ngOnInit(): void {
    this.setBreadcrumbItems();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.profile$ = this.orderHistoryListService.getProfileData$();

    this.pageTitle = this.titleService.getTitle();

    combineLatest([
      this.personalisationService.getPersonalData$(),
      this.orderHistoryListService.getProfileData$(),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        // filter((profile: IUserProfile): boolean => !!profile?.isLoggedIn),
        mergeMap(([personalData, profile]) => {
          const globalId = personalData?.identifiers?.globalId;
          if (profile.isLoggedIn && globalId) {
            profile.profileInfo.contactGlobalId = globalId;
          }
          return this.orderHistoryListService
            .getOrderHistoryList(profile.profileInfo)
            .pipe(shareReplay(1), retry(1));
        })
      )
      .subscribe(
        (item) => {
          if (item && this.orderHistoryListService.pagedData) {
            this.orderHistoryList = item;
            this.orderHistoryListService.pagedData.records =
              item?.orderHistDetail;
          }
        },
        (errorResponse) => {
          logger.error(
            '[literatureDataOrderHistory] error while getting data from backend service : ' +
              JSON.stringify(errorResponse) +
              ' [error status]: ' +
              (errorResponse.status || 'N/A')
          );
        }
      );

    this.columnDefs = this.getColumnDefs();
    this.rowNoItems = this.createDropDownRowNoItems();
    this.sortByItems = this.createSortByDropDownItems();
    this.handlePagination();
    this.changeDetectorRef.detectChanges();
  }

  private handlePagination(): void {
    this.paginationPageSize = this.orderHistoryListService.getPageSize();
    if (this.orderHistoryListService.getNumberOfPages$()) {
      this.orderHistoryListService
        .getNumberOfPages$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((data: number) => {
          this.numberOfPages = data;
          this.paginationComponent?.updatePagination(this.numberOfPages);
          this.pageSize = this.rowNoItems.find(
            (options) =>
              Number(options.value) ===
              this.orderHistoryListService.getPageSize()
          );
          this.changeDetectorRef.detectChanges();
        });
    }
    if (this.orderHistoryListService.getCurrentPage$()) {
      this.orderHistoryListService
        .getCurrentPage$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((pageData: OrderHistDetail[]): void => {
          this.currentPage = pageData;
          this.changeDetectorRef.detectChanges();
        });
    }
    if (this.orderHistoryListService.getPageNumber$()) {
      this.orderHistoryListService
        .getPageNumber$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((n: number): void => {
          this.orderHistoryListGrid?.api.paginationGoToPage(n - 1);
        });
    }
  }

  getInfo(
    ItemName: string,
    data: OrderHistDetail
  ): { label?: string; val?: string } {
    if (ItemName === 'Item') {
      return {
        label: this.translateService.instant('literature.lit-listing-items'),
      };
    } else if (ItemName === 'Date') {
      return {
        label: this.translateService.instant('literature.lit-order-date'),
        val: this.dateFormatter.transform(
          data.orderDate,
          this.orderHistoryListService.dateFormat
        ),
      };
    } else if (ItemName === 'status') {
      return {
        label: this.translateService.instant('literature.lit-order-status'),
        val: data.orderStatus,
      };
    } else if (ItemName === 'orderSource') {
      return {
        label: this.translateService.instant(
          'literature.lit-order-online-phone'
        ),
        val: data.orderSource,
      };
    }
  }

  getColumnDefs() {
    const actionBaseLink = `${this.literatureConfig?.navigationUrls?.litOrderHistoryDetailUrl}`;
    return [
      {
        headerName: this.translateService.instant('literature.lit-order-date'),
        colId: 'ORDER-DATE',
        cellClass: ['ag-cell-frk--highlightedPrimary'],
        minWidth: 300,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        sort: 'desc',
        valueGetter: (params) => {
          return `${params.data.orderDate}`;
        },
        valueFormatter: (params) => {
          if (params.value !== 'undefined') {
            return this.dateFormatter.transform(
              params.value,
              this.orderHistoryListService.dateFormat
            );
          }
          return '-';
        },
        comparator: createDateComparator(this.apiDateFormat),
        flex: 1,
      },
      {
        headerName: this.translateService.instant(
          'literature.lit-order-number'
        ),
        colId: 'ACTIONS',
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        cellClass: ['ag-cell-frk--highlightedPrimary'],
        width: 300,
        cellRenderer: 'customizedOrderNumber',
        cellRendererParams: {
          actionBaseLink,
        },
      },
      {
        headerName: this.translateService.instant(
          'literature.lit-order-status'
        ),
        colId: 'STATUS',
        cellClass: ['ag-cell-frk--highlightedPrimary'],
        minWidth: 180,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        valueGetter: (params) => {
          return `${params.data.orderStatus}`;
        },
        flex: 1,
      },
      {
        headerName: this.translateService.instant(
          'literature.lit-order-online-phone'
        ),
        colId: 'ORDEREDBY',
        cellClass: ['ag-cell-frk--highlightedPrimary'],
        minWidth: 100,
        sortable: true,
        valueGetter: (params) => {
          return `${params.data.orderSource}`;
        },
        flex: 1,
      },
    ];
  }

  onSorting(event): void {
    this.orderHistoryList = this.sortData(this.orderHistoryList, event.value);
  }

  getSortByItemsDef() {
    return this.getColumnDefs().filter((colItem) => {
      return (
        colItem.colId === 'ORDER-DATE' ||
        colItem.colId === 'STATUS' ||
        colItem.colId === 'ACTIONS'
      );
    });
  }

  createSortByDropDownItems() {
    let sortByItemValues = [];
    const sortByItemsDef = this.getSortByItemsDef();
    sortByItemValues = sortByItemsDef.map((colDefItem) => {
      return {
        value: colDefItem.colId,
        label: colDefItem.headerName,
      };
    });
    return sortByItemValues;
  }

  sortData(data: OrderHistoryList, selectedValue: string): OrderHistoryList {
    if (data) {
      if (selectedValue === 'ACTIONS') {
        const nameCompare = stringCompare;
        data.orderHistDetail = data?.orderHistDetail.sort((current, next) => {
          return nameCompare(
            current.orderNumber,
            next.orderNumber,
            null,
            null,
            false
          );
        });
        return data;
      } else if (selectedValue === 'ORDER-DATE') {
        const dateCompare = createDateComparator(this.apiDateFormat);
        data.orderHistDetail = data.orderHistDetail
          .sort((current, next) => {
            return dateCompare(
              current.orderDate,
              next.orderDate,
              null,
              null,
              false
            );
          })
          .reverse();
        return data;
      } else if (selectedValue === 'STATUS') {
        const nameCompare = stringCompare;
        data.orderHistDetail = data.orderHistDetail.sort((current, next) => {
          return nameCompare(
            current.orderStatus,
            next.orderStatus,
            null,
            null,
            false
          );
        });
        return data;
      }
    }
  }

  createDropDownRowNoItems() {
    const dropDownRows: string[] = this.translateService
      .instant('literature.lit-order-page-size')
      .split(',');
    let dropDownRowsNoItems = [];
    if (dropDownRows) {
      dropDownRowsNoItems = dropDownRows.map((item) => {
        return {
          value: item,
          label: `${item} ${this.translateService.instant(
            'literature.lit-order-per-page'
          )}`,
        };
      });
    }
    return dropDownRowsNoItems;
  }

  onChange(numberOfRowsEvent) {
    this.orderHistoryListService.updateNumberOfRows(
      Number(numberOfRowsEvent.value)
    );
    this.orderHistoryListGrid?.api?.paginationSetPageSize(
      Number(numberOfRowsEvent.value)
    );
  }

  onPageChanged(event): void {
    this.scrollService.scrollToAnchor('orderHistoryScrollPosition');
    this.orderHistoryListService.goToPage(event.page);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
